/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, { BORDER_WIDTH } from './WireframeBase';
import tools from '../tools';
import wireframePlaceholder from '../assets/icons/wireframePlaceholder.svg';
import wireframePlaceholderImageMenuIcon from '../assets/icons/wireframePlaceholderImageMenuIcon.svg';
import wireframeImage from '../assets/icons/wireframeImage.svg';
import wireframeVideo from '../assets/icons/wireframeVideo.svg';
import wireframeMap from '../assets/icons/wireframeMap.svg';
import wireframePieChart from '../assets/icons/wireframePieChart.svg';
import wireframeDonutChart from '../assets/icons/wireframeDonutChart.svg';
import wireframeBarChart from '../assets/icons/wireframeBarChart.svg';
import wireframeLineChart from '../assets/icons/wireframeLineChart.svg';
import wireframePlaceholderImage from '../assets/wireframes/wireframePlaceholderImage.svg';
import wireframePlaceholderVideo from '../assets/wireframes/wireframePlaceholderVideo.svg';
import wireframePlaceholderMap from '../assets/wireframes/wireframePlaceholderMap.svg';
import wireframePlaceholderPieChart from '../assets/wireframes/wireframePlaceholderPieChart.svg';
import wireframePlaceholderDonutChart from '../assets/wireframes/wireframePlaceholderDonutChart.svg';
import wireframePlaceholderBarChart from '../assets/wireframes/wireframePlaceholderBarChart.svg';
import wireframePlaceholderLineChart from '../assets/wireframes/wireframePlaceholderLineChart.svg';
import { Rect } from '@lib/canvasx/components/Rect';
import { Image } from '@lib/canvasx/components/Image';
import { Canvas } from '@lib/canvasx/components/Canvas';
import { svgToURL } from '../utils/svg';
import Color from 'color';
import WireframeLinearResizeControl from '../controls/WireframeLinearResizeControl';

const IMAGE_STYLE_SVGS = {
  image: wireframePlaceholderImage,
  video: wireframePlaceholderVideo,
  map: wireframePlaceholderMap,
  pieChart: wireframePlaceholderPieChart,
  donutChart: wireframePlaceholderDonutChart,
  barChart: wireframePlaceholderBarChart,
  lineChart: wireframePlaceholderLineChart,
} as const;

// A placeholder image. Can also display some comnmon image types like
// charts and graphs.
class WireframeImage extends WireframeBase {
  constructor(fabricObject) {
    super(fabricObject);

    fabricObject.style ||= 'image';

    // Override the wireframe default behavior of uniform scaling.
    fabricObject.uniformScaling = false;
  }

  tools() {
    return [
      [tools.ToolWireframeName],
      [tools.ToolSize],
      [tools.ToolFillColorWithoutOpacity],
    ];
  }

  static defaultWidth(): number {
    return 150;
  }

  static defaultHeight(): number {
    return 150;
  }

  static iconSvg() {
    return wireframePlaceholderImageMenuIcon;
  }

  static externalName(): string {
    return 'Image';
  }

  static wireframeControls = this.createDefaultControls();
  static createDefaultControls() {
    const controls = super.createDefaultControls();
    return {
      ...controls,
      ml: new WireframeLinearResizeControl({ edge: 'left' }),
      mr: new WireframeLinearResizeControl({ edge: 'right' }),
      mt: new WireframeLinearResizeControl({ edge: 'top' }),
      mb: new WireframeLinearResizeControl({ edge: 'bottom' }),
    };
  }

  minDimension() {
    return 20;
  }

  imageSVGContent() {
    // Colorize the SVG based on the fill color.
    const svg = IMAGE_STYLE_SVGS[this.wireframeName];
    const fillColor = this.filled ? '#FFFFFF' : this.fabricObject.fill;
    return svg.replace(/fill="currentColor"/g, `fill="${fillColor}"`);
  }

  static wireframeNames() {
    return {
      placeholder: wireframePlaceholder,
      image: wireframeImage,
      video: wireframeVideo,
      map: wireframeMap,
      pieChart: wireframePieChart,
      donutChart: wireframeDonutChart,
      barChart: wireframeBarChart,
      lineChart: wireframeLineChart,
    };
  }

  wireframeNames() {
    return WireframeImage.wireframeNames();
  }

  render(ctx: CanvasRenderingContext2D) {
    const { width, height, scaleX, scaleY } = this.fabricObject;
    const { filled } = this;
    const fill = this.fabricObject.fill || '#333333';
    const backgroundColor = Color(fill).lightness(97);
    const borderWidth = filled ? 1 : BORDER_WIDTH;
    const scaledWidth = width * scaleX;
    const scaledHeight = height * scaleY;
    const imageSize = Math.min(scaledWidth, scaledHeight) * 0.8;

    let contents;
    if (this.wireframeName === 'placeholder') {
      const offset = 1.5;
      contents = (
        <Canvas
          render={ctx => {
            ctx.beginPath();
            ctx.moveTo(offset, offset);
            ctx.lineTo(scaledWidth - offset, scaledHeight - offset);
            ctx.moveTo(scaledWidth - offset, offset);
            ctx.lineTo(offset, scaledHeight - offset);
            ctx.lineWidth = borderWidth;
            ctx.strokeStyle = fill;
            ctx.lineCap = 'round';
            ctx.stroke();
          }}
        />
      );
    } else {
      contents = (
        <Image
          src={svgToURL(this.imageSVGContent())}
          x={(scaledWidth - imageSize) / 2}
          y={(scaledHeight - imageSize) / 2}
          width={imageSize}
          height={imageSize}
        />
      );
    }

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: 1,
      },
      () => {
        renderElement(
          <Rect
            width={scaledWidth}
            height={scaledHeight}
            fill={{
              color: backgroundColor.hex(),
            }}
            border={{
              color: fill,
              lineWidth: borderWidth,
            }}
            borderRadius={5}
          >
            {contents}
          </Rect>
        );
      }
    );
  }
}

export default WireframeImage;
